// src/TopicsPage.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { parseEther, keccak256, toUtf8Bytes, concat, AbiCoder, ZeroHash } from "ethers";
import { FaPlusCircle, FaMinusSquare } from 'react-icons/fa'; // Import icons

function hashOptionIndexAndSecret(optionIndex, secret) {
    // Initialize the AbiCoder
    const abiCoder = new AbiCoder();

    let encodedOptionIndex;
    // Encode the optionIndex as a uint256 and the secret as a string
    if (optionIndex === 0) {
        // Special case for 0 to ensure it is encoded as "0x00"
        encodedOptionIndex = "0x00";
    } else
        encodedOptionIndex = abiCoder.encode(["uint8"], [optionIndex]).replace(/^0x(00)+/, '0x');
    const encodedSecret = toUtf8Bytes(secret);

    // Concatenate the encoded values
    const concatenatedData = concat([encodedOptionIndex, encodedSecret]);
    // Compute and return the keccak256 hash
    return keccak256(concatenatedData);
}

function CreateTopic({ contract, signerContract }) {
    // const [topics, setTopics] = useState([]);
    const [newTopic, setNewTopic] = useState({
        question: "",
        options: ["", ""],  // Ensure at least two options are present initially
        duplicateOptions: [], // Track which options are duplicates
        startDate: "",
        endDate: "",
        bonusCoins: "",
        minBetAmount: "", // Add minBetAmount to state
        maxBetAmount: "", // Ensure maxBetAmount is in state
        isPredetermined: false,
        correctOptionIndex: 0,
        correctOptionHash: "",
        secret: "",
        rateAllocation: {
            toWinners: "85.00", // Store as a string to allow decimal input
            toCreator: "10.00",
            destroyed: "5.00",
        },
    });
    const [rateAllocationError, setRateAllocationError] = useState("");
    const [message, setMessage] = useState("");
    const [log, setLog] = useState([]);

    const handleCreateTopic = async () => {
        if (!contract) return;

        // Check for duplicate options
        const optionsSet = new Set(newTopic.options);
        if (optionsSet.size !== newTopic.options.length) {
            setMessage("Error: Duplicate options are not allowed.");
            return; // Exit early if there are duplicate options
        }

        let correctOptionHash = ZeroHash;
        if (newTopic.isPredetermined) {
            correctOptionHash = hashOptionIndexAndSecret(newTopic.correctOptionIndex, newTopic.secret);
        }
        // Convert percentage values to their required format (out of 10000)
        const toWinners = Math.round(parseFloat(newTopic.rateAllocation.toWinners) * 100);
        const toCreator = Math.round(parseFloat(newTopic.rateAllocation.toCreator) * 100);
        const destroyed = Math.round(parseFloat(newTopic.rateAllocation.destroyed) * 100);

        const totalRateAllocation = toWinners + toCreator + destroyed;

        // Check if total rate allocation equals 100%
        if (totalRateAllocation !== 10000) {
            setRateAllocationError("The total rate allocation must equal 100%.");
            return; // Exit early if the allocation is incorrect
        } else {
            setRateAllocationError(""); // Clear error if the allocation is correct
        }

        const rateAllocation = {
            toWinners: toWinners,
            toCreator: toCreator,
            destroyed: destroyed,
        };


        // Create the Timing struct
        const timing = {
            startDate: Math.floor(new Date(newTopic.startDate).getTime() / 1000),
            endDate: Math.floor(new Date(newTopic.endDate).getTime() / 1000),
        };
        // // Log all the parameters before calling the contract
        // console.log("Question:", newTopic.question);
        // console.log("Options:", newTopic.options);
        // console.log("Start Date (Unix):", Math.floor(new Date(newTopic.startDate).getTime() / 1000));
        // console.log("End Date (Unix):", Math.floor(new Date(newTopic.endDate).getTime() / 1000));
        // console.log("Rate Allocation (out of 10000):", rateAllocation);
        // console.log("Is Predetermined:", newTopic.isPredetermined);
        // console.log("Correct Option Hash:", correctOptionHash);
        // console.log("Bonus Coins (in Wei):", parseEther(newTopic.bonusCoins).toString());

        try {
            const tx = await signerContract.createTopic(
                newTopic.question,
                newTopic.options,
                timing, // Pass the timing struct
                rateAllocation,
                newTopic.isPredetermined,
                correctOptionHash,
                parseEther(newTopic.maxBetAmount || "0"), // Pass maxBetAmount, default to 0 if empty
                parseEther(newTopic.minBetAmount || "0"), // Pass minBetAmount, default to 0 if empty
                {
                    value: parseEther(newTopic.bonusCoins), // Pass the bonusCoins as msg.value
                    // gasLimit: 1000000, // Example gas limit
                }
            );
            await tx.wait();
            const newLog = {
                action: "Create  Topic",
                timestamp: new Date().toLocaleString(),
                status: "success",
            };
            setLog([newLog, ...log.slice(0, 4)]);
            setMessage(" Topic created successfully!");
            // loadTopics(contract);
        } catch (error) {
            console.error("Error creating topic:", error);
            const newLog = {
                action: "Create  Topic",
                timestamp: new Date().toLocaleString(),
                status: "Failed with error: " + (error.message || "Unknown error"),
            };
            setLog([newLog, ...log.slice(0, 4)]);
            setMessage("Failed to create  Topic.");
        }
    };
    const handleAddOption = () => {
        setNewTopic({ ...newTopic, options: [...newTopic.options, ""], duplicateOptions: [] });
    };

    const handleRemoveOption = (index) => {
        if (newTopic.options.length > 2) {
            const updatedOptions = newTopic.options.filter((_, i) => i !== index);
            setNewTopic({ ...newTopic, options: updatedOptions, duplicateOptions: [] });
        }
    };

    const handleOptionChange = (value, index) => {
        const updatedOptions = [...newTopic.options];
        updatedOptions[index] = value;

        // Detect duplicates
        const duplicates = updatedOptions.filter((item, idx) => updatedOptions.indexOf(item) !== idx && item !== "");

        const duplicateIndices = updatedOptions.map((item, idx) =>
            duplicates.includes(item) ? idx : null
        ).filter((idx) => idx !== null);

        setNewTopic({
            ...newTopic,
            options: updatedOptions,
            duplicateOptions: duplicateIndices,
        });
    };

    return (
        <div className="container mt-5">
            <Helmet>
                <title>6Degree - Create a New  Topic on the Blockchain</title>
                <meta name="description" content="Start a new  topic on 6Degree. Engage participants with decentralized, transparent, and secure blockchain betting." />
            </Helmet>
            <h1>Create a New  Topic</h1>
            <div className="mb-4">
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Question"
                    value={newTopic.question}
                    onChange={(e) =>
                        setNewTopic({ ...newTopic, question: e.target.value })
                    }
                />
                {newTopic.options.map((option, index) => (
                    <div key={index} className="d-flex align-items-center mb-2">
                        <input
                            type="text"
                            className={`form-control ${newTopic.duplicateOptions.includes(index) ? "is-invalid" : ""}`}
                            placeholder={`Option ${index + 1}`}
                            value={option}
                            onChange={(e) => handleOptionChange(e.target.value, index)}
                        />
                        {newTopic.options.length > 2 && (
                            <button
                                className="btn btn-danger ml-2"
                                onClick={() => handleRemoveOption(index)}
                            >
                                <FaMinusSquare />
                            </button>
                        )}
                    </div>
                ))}
                <button className="btn btn-primary mb-2" onClick={handleAddOption}>
                    <FaPlusCircle />
                </button>
                <div className="d-flex align-items-center">
                    <label className="mr-2">Start date:</label>
                    <input
                        type="datetime-local"
                        className="form-control mb-2"
                        placeholder="Start Date"
                        value={newTopic.startDate}
                        onChange={(e) =>
                            setNewTopic({ ...newTopic, startDate: e.target.value })
                        }
                    />
                </div>
                <div className="d-flex align-items-center">
                    <label className="mr-2">End date:</label>
                    <input
                        type="datetime-local"
                        className="form-control mb-2"
                        placeholder="End Date"
                        value={newTopic.endDate}
                        onChange={(e) =>
                            setNewTopic({ ...newTopic, endDate: e.target.value })
                        }
                    />
                </div>
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Bonus Coins (6DO)"
                    value={newTopic.bonusCoins}
                    onChange={(e) =>
                        setNewTopic({ ...newTopic, bonusCoins: e.target.value })
                    }
                />
                <div className="mb-2">
                    <h4>Rate Allocation (%)</h4>
                    <div className="d-flex align-items-center">
                        <label className="mr-2">To Winners:</label>
                        <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder="e.g. 85.00"
                            value={newTopic.rateAllocation.toWinners}
                            onChange={(e) =>
                                setNewTopic({
                                    ...newTopic,
                                    rateAllocation: {
                                        ...newTopic.rateAllocation,
                                        toWinners: e.target.value,
                                    },
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center">
                        <label className="mr-2">To Creator:</label>
                        <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder="e.g. 10.00"
                            value={newTopic.rateAllocation.toCreator}
                            onChange={(e) =>
                                setNewTopic({
                                    ...newTopic,
                                    rateAllocation: {
                                        ...newTopic.rateAllocation,
                                        toCreator: e.target.value,
                                    },
                                })
                            }
                        />
                    </div>
                    <div className="d-flex align-items-center">
                        <label className="mr-2">Destroyed:</label>
                        <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            placeholder="e.g. 5.00"
                            value={newTopic.rateAllocation.destroyed}
                            onChange={(e) =>
                                setNewTopic({
                                    ...newTopic,
                                    rateAllocation: {
                                        ...newTopic.rateAllocation,
                                        destroyed: e.target.value,
                                    },
                                })
                            }
                        />
                    </div>
                </div>

                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Min Bet Amount (6DO)" // Add input for minBetAmount
                    value={newTopic.minBetAmount}
                    onChange={(e) =>
                        setNewTopic({ ...newTopic, minBetAmount: e.target.value })
                    }
                />
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Max Bet Amount (6DO)"
                    value={newTopic.maxBetAmount}
                    onChange={(e) =>
                        setNewTopic({ ...newTopic, maxBetAmount: e.target.value })
                    }
                />
                <div className="form-check mb-2">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newTopic.isPredetermined}
                        onChange={(e) =>
                            setNewTopic({
                                ...newTopic,
                                isPredetermined: e.target.checked,
                            })
                        }
                    />
                    <label className="form-check-label">
                        Is Predetermined?
                    </label>
                </div>
                {newTopic.isPredetermined && (
                    <>
                        <select
                            className="form-control mb-2"
                            value={newTopic.correctOptionIndex}
                            onChange={(e) =>
                                setNewTopic({ ...newTopic, correctOptionIndex: e.target.value })
                            }
                        >
                            {newTopic.options.map((option, index) => (
                                <option key={index} value={index}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        <input
                            type="text"
                            className="form-control mb-2"
                            placeholder="Secret Key"
                            value={newTopic.secret}
                            onChange={(e) =>
                                setNewTopic({ ...newTopic, secret: e.target.value })
                            }
                        />
                    </>
                )}
                {rateAllocationError && (
                    <div className="alert alert-danger mt-2">
                        {rateAllocationError}
                    </div>
                )}
                <button onClick={handleCreateTopic} className="btn btn-success">
                    Create  Topic
                </button>
            </div>

            {message && <div className="alert alert-info mt-4">{message}</div>}
            <div className="mt-5">
                <h4>Log</h4>
                <ul>
                    {log.map((entry, index) => (
                        <li key={index} style={{ color: entry.status === "fail" ? "red" : "black" }}>
                            [{entry.timestamp}] {entry.action} - {entry.address} - {entry.status}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default CreateTopic;